import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { AxiosResponse } from 'axios'

import { loadingThunk } from '#/app/store/store'
import { getApiUrl } from '#/shared/lib/constants'

type UserState = {
	status: loadingThunk
	referral: string
}

export type ResponseAllInfo = {
	uid: string
	first_name: string
	last_name: string
	created_at: string
	email: string
	is_active: boolean
	is_staff: boolean
	show_balance: boolean
	is_confirmed: boolean
	is_social: boolean
	social_auth: string[]
	is_subscribed_to_emails: boolean
	profile_picture_link: string | null
	account_type: string
	referral_code: {
		code: string
	}
	token: {
		access: string
		refresh: string
	}
	payment_plan: {
		uid: string
		is_recurring: boolean
		plan: {
			uid: string
			price: string
			individual: boolean
			tokens_per_plan: string
			is_corporate: boolean
			title: string
			duration: string
			accessed_models: string[] | null
		}
		last_payment_at: string
		next_payment_at: string
		cutoff_at: string | null
		last_recurrent_failed: boolean
		current_token_balance: number
	}
}

const initialState: UserState & ResponseAllInfo = {
	status: 'idle',
	referral: '',
	uid: '',
	first_name: '',
	last_name: '',
	created_at: '',
	email: '',
	show_balance: true,
	is_active: false,
	social_auth: [],
	is_staff: false,
	is_confirmed: false,
	referral_code: {
		code: '',
	},
	token: {
		access: '',
		refresh: '',
	},
	is_subscribed_to_emails: false,
	is_social: false,
	profile_picture_link: null,
	account_type: 'regular',
	payment_plan: {
		uid: '',
		is_recurring: false,
		plan: {
			uid: '',
			price: '',
			tokens_per_plan: '',
			is_corporate: false,
			individual: false,
			duration: '',
			title: '',
			accessed_models: null,
		},
		last_payment_at: '',
		next_payment_at: '',
		cutoff_at: null,
		last_recurrent_failed: false,
		current_token_balance: 0,
	},
}

export const getAll = async (token: string | null | undefined): Promise<ResponseAllInfo> => {
	const { data } = await axios.get<any, AxiosResponse<ResponseAllInfo>>(getApiUrl() + '/auth/me', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		validateStatus: (status) => status === 200,
	})

	return data
}

export const getAllInfo = createAsyncThunk('user/getAllInfo', async (token: string | null | undefined) => {
	return await getAll(token)
})

export const unfollowEmail = createAsyncThunk('user/unfollowEmail', async (token: string | null | undefined) => {
	await axios.patch(getApiUrl() + '/auth/email-sub', {}, { headers: { Authorization: `Bearer ${token}` } })
})

export const userSlice = createSlice({
	name: 'userSlice',
	initialState,
	reducers: {
		addReferral: (state, action) => {
			state.referral = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getAllInfo.fulfilled, (state, action) => {
			Object.assign(state, action.payload)
			state.status = 'succeeded'
			state.referral = ''
		})
		builder.addCase(getAllInfo.pending, (state) => {
			state.status = 'pending'
		})
		builder.addCase(getAllInfo.rejected, (state) => {
			state.status = 'failed'
		})
		builder.addCase(unfollowEmail.fulfilled, (state) => {
			state.is_subscribed_to_emails = !state.is_subscribed_to_emails
		})
	},
})

export const { addReferral } = userSlice.actions
